import { create } from 'zustand';
import type { 
  GameState, GameMode, PlayerAction, SeatPlayer
} from '@/types';
import { TexasHoldemEngine } from '@/engine/game-state';
import { makeAIDecision, recordHumanAction } from '@/ai';
import { GAME_CONFIGS, SCORE_REWARDS } from '@/utils/constants';
import { usePlayerStore } from './player-store';

interface GameStore {
  // 游戏状态
  gameState: GameState | null;
  engine: TexasHoldemEngine | null;
  
  // UI 状态
  isLoading: boolean;
  error: string | null;
  showCardReveal: boolean;
  winnerIds: string[];
  handResult: { playerId: string; handName: string }[];
  
  // 倒计时
  timeLeft: number;
  maxTime: number;
  
  // 游戏设置
  gameMode: GameMode;
  
  // 操作
  initializeGame: (mode: GameMode, humanPlayer: { id: string; name: string; avatar: string }) => void;
  startNewHand: () => void;
  performAction: (action: PlayerAction) => void;
  processAITurn: () => Promise<void>;
  handleSettlement: () => void;
  endGame: () => Promise<void>;
  resetGame: () => void;
  resetTimer: () => void;
  
  // 获取器
  getCurrentPlayer: () => SeatPlayer | null;
  getValidActions: () => string[];
  getPotSize: () => number;
  getHumanPlayer: () => SeatPlayer | null;
}

const TURN_TIME_LIMIT = 15; // 15秒倒计时

export const useGameStore = create<GameStore>((set, get) => ({
  gameState: null,
  engine: null,
  isLoading: false,
  error: null,
  showCardReveal: false,
  winnerIds: [],
  handResult: [],
  timeLeft: TURN_TIME_LIMIT,
  maxTime: TURN_TIME_LIMIT,
  gameMode: 'casual',

  initializeGame: (mode: GameMode, humanPlayer) => {
    const engine = new TexasHoldemEngine(mode, humanPlayer);
    const initialState = engine.startNewHand();
    
    set({
      engine,
      gameState: initialState,
      gameMode: mode,
      error: null,
      showCardReveal: false,
      winnerIds: [],
      handResult: [],
      timeLeft: TURN_TIME_LIMIT,
    });

    // 扣除入场费
    const config = GAME_CONFIGS[mode];
    if (config.entryFee > 0) {
      usePlayerStore.getState().deductChips(config.entryFee);
    }

    // 检查是否需要 AI 先行动
    const currentPlayer = initialState.players[initialState.currentPlayerIndex];
    if (!currentPlayer.isHuman) {
      get().processAITurn();
    }
  },

  startNewHand: () => {
    const { engine } = get();
    if (!engine) return;

    const newState = engine.startNewHand();
    set({
      gameState: newState,
      showCardReveal: false,
      winnerIds: [],
      handResult: [],
      timeLeft: TURN_TIME_LIMIT,
    });

    // 检查是否需要 AI 先行动
    const currentPlayer = newState.players[newState.currentPlayerIndex];
    if (!currentPlayer.isHuman) {
      get().processAITurn();
    }
  },

  performAction: (action: PlayerAction) => {
    const { engine, gameState } = get();
    if (!engine || !gameState) return;

    const currentPlayer = gameState.players[gameState.currentPlayerIndex];
    if (!currentPlayer.isHuman) return;

    // 执行动作
    const newState = engine.handleAction(currentPlayer.id, action);
    set({ gameState: newState, timeLeft: TURN_TIME_LIMIT });

    // 记录人类玩家动作（用于 AI 学习）
    recordHumanAction(gameState, currentPlayer.id, action, gameState.phase);

    // 检查游戏阶段
    if (newState.phase === 'settlement') {
      get().handleSettlement();
    } else {
      // 继续 AI 回合
      get().processAITurn();
    }
  },

  processAITurn: async () => {
    const { engine, gameState } = get();
    if (!engine || !gameState) return;

    const processNextAI = async () => {
      const currentState = get().gameState;
      if (!currentState) return;

      const currentPlayer = currentState.players[currentState.currentPlayerIndex];
      
      // 如果是人类玩家，重置计时器并停止
      if (currentPlayer.isHuman) {
        set({ timeLeft: TURN_TIME_LIMIT });
        return;
      }
      
      // 如果游戏已结束，停止
      if (currentState.phase === 'settlement' || currentState.phase === 'waiting') return;

      // AI 决策
      await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 1000)); // 模拟思考时间
      
      const action = makeAIDecision(
        currentState, 
        currentPlayer.id, 
        currentPlayer.aiLevel || 'easy'
      );

      const newState = engine.handleAction(currentPlayer.id, action);
      set({ gameState: newState });

      // 检查游戏阶段
      if (newState.phase === 'settlement') {
        get().handleSettlement();
      } else {
        // 继续下一个 AI
        const nextPlayer = newState.players[newState.currentPlayerIndex];
        if (!nextPlayer.isHuman) {
          await processNextAI();
        } else {
          // 轮到人类玩家，重置计时器
          set({ timeLeft: TURN_TIME_LIMIT });
        }
      }
    };

    await processNextAI();
  },

  handleSettlement: () => {
    const { gameState, engine } = get();
    if (!gameState || !engine) return;

    // 显示结果
    const activePlayers = gameState.players.filter(
      p => p.status !== 'folded' && p.status !== 'eliminated'
    );

    if (activePlayers.length > 1) {
      // 摊牌比大小
      set({ 
        showCardReveal: true,
        handResult: activePlayers.map(p => ({
          playerId: p.id,
          handName: '未知', // 实际应该计算牌型名称
        })),
      });
    }

    // 检查游戏是否结束（锦标赛模式）
    if (engine.isGameOver()) {
      setTimeout(() => get().endGame(), 3000);
    }
  },

  endGame: async () => {
    const { gameState, gameMode } = get();
    if (!gameState) return;

    const currentProfile = usePlayerStore.getState().currentProfile;
    if (!currentProfile) return;

    // 计算排名
    const sortedPlayers = [...gameState.players].sort((a, b) => b.chips - a.chips);
    const placement = sortedPlayers.findIndex(p => p.id === currentProfile.id) + 1;
    
    // 计算筹码变化
    const humanPlayer = gameState.players.find(p => p.id === currentProfile.id);
    const chipChange = humanPlayer ? humanPlayer.chips - (GAME_CONFIGS[gameMode].startingChips || 0) : 0;

    // 计算积分
    let scoreChange = 0;
    if (gameMode.startsWith('tournament')) {
      const mode = gameMode as 'tournament_low' | 'tournament_mid' | 'tournament_high';
      const rewards = SCORE_REWARDS[mode];
      if (placement === 1) scoreChange = rewards.first;
      else if (placement === 2) scoreChange = rewards.second;
      else if (placement === 3) scoreChange = rewards.third;
    }

    // 连胜奖励
    const currentStreak = currentProfile.stats.currentStreak;
    if (placement === 1) {
      if (currentStreak >= 10) scoreChange += SCORE_REWARDS.streak_10;
      else if (currentStreak >= 5) scoreChange += SCORE_REWARDS.streak_5;
      else if (currentStreak >= 3) scoreChange += SCORE_REWARDS.streak_3;
    }

    // 创建游戏记录
    const record = {
      mode: gameMode,
      placement,
      chipChange,
      scoreChange,
      handsPlayed: gameState.handNumber,
      highlights: [],
    };

    await usePlayerStore.getState().recordGame(record);

    // 重置游戏状态
    set({
      gameState: null,
      engine: null,
      showCardReveal: false,
      winnerIds: [],
      handResult: [],
    });
  },

  resetGame: () => {
    set({
      gameState: null,
      engine: null,
      error: null,
      showCardReveal: false,
      winnerIds: [],
      handResult: [],
      timeLeft: TURN_TIME_LIMIT,
    });
  },

  resetTimer: () => {
    set({ timeLeft: TURN_TIME_LIMIT });
  },

  getCurrentPlayer: () => {
    const { gameState } = get();
    if (!gameState) return null;
    return gameState.players[gameState.currentPlayerIndex];
  },

  getValidActions: () => {
    const { engine, gameState } = get();
    if (!engine || !gameState) return [];
    
    const currentPlayer = gameState.players[gameState.currentPlayerIndex];
    if (!currentPlayer.isHuman) return [];
    
    return engine.getValidActions(currentPlayer.id);
  },

  getPotSize: () => {
    const { gameState } = get();
    if (!gameState) return 0;
    return gameState.pots.reduce((sum, pot) => sum + pot.amount, 0);
  },

  getHumanPlayer: () => {
    const { gameState } = get();
    if (!gameState) return null;
    return gameState.players.find(p => p.isHuman) || null;
  },
}));
